import React, { useState, useEffect } from 'react';
import { FileText } from 'lucide-react';
import { openCorporateQuoteWindow } from './adminUtils';

const DraftsTab = ({ BACKEND_URL }) => {
  const [unsubmittedQuotes, setUnsubmittedQuotes] = useState([]);

  const fetchUnsubmittedQuotes = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/unsubmitted-quotes`);
      if (!response.ok) throw new Error('Failed to fetch unsubmitted quotes');
      const data = await response.json();
      setUnsubmittedQuotes(data);
    } catch (err) {
      console.error('Error fetching unsubmitted quotes:', err.message);
      setUnsubmittedQuotes([]);
    }
  };

  useEffect(() => { fetchUnsubmittedQuotes(); }, []);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h2 data-testid="drafts-title" className="text-2xl font-bold text-[#1a4d2e]">Unsubmitted Quotes</h2>
            <p className="text-gray-600">All quotes that have not yet been submitted for processing</p>
          </div>
          <div className="flex gap-2">
            {unsubmittedQuotes.length > 0 && (
              <button
                onClick={async () => {
                  if (window.confirm(`Are you sure you want to delete ALL ${unsubmittedQuotes.length} unsubmitted quotes? This cannot be undone.`)) {
                    try {
                      const response = await fetch(`${BACKEND_URL}/api/admin/unsubmitted-quotes/clear-all`, { method: 'DELETE' });
                      if (response.ok) {
                        const result = await response.json();
                        alert(`${result.deleted_count} draft quotes deleted`);
                        fetchUnsubmittedQuotes();
                      } else {
                        alert('Error clearing drafts');
                      }
                    } catch (err) {
                      alert('Error: ' + err.message);
                    }
                  }
                }}
                data-testid="clear-all-drafts-btn"
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm font-medium"
              >
                Clear All Drafts
              </button>
            )}
            <button
              onClick={fetchUnsubmittedQuotes}
              data-testid="refresh-drafts-btn"
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-sm font-medium"
            >
              Refresh
            </button>
          </div>
        </div>

        {unsubmittedQuotes.length === 0 ? (
          <div className="text-center py-8 bg-gray-50 rounded-lg">
            <div className="text-4xl mb-3">No drafts</div>
            <p className="text-gray-600">No unsubmitted quotes found</p>
            <p className="text-sm text-gray-500">All quotes have been submitted</p>
          </div>
        ) : (
          <div className="space-y-4">
            {unsubmittedQuotes.map((quote) => (
              <div key={quote.id} className="border border-gray-200 rounded-lg p-4 hover:border-[#1a4d2e]/30 transition-colors">
                <div className="flex flex-col lg:flex-row lg:items-start gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="font-semibold text-gray-900">
                        {quote.farm_details?.farm_name || 'Unnamed Quote'}
                      </h3>
                      <span className="bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full text-xs font-medium">
                        Draft
                      </span>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 text-sm">
                      <div>
                        <span className="text-gray-500">Created:</span>
                        <p className="font-medium">
                          {quote.created_at
                            ? new Date(quote.created_at).toLocaleDateString('en-NZ', { day: 'numeric', month: 'short', year: 'numeric' })
                            : '-'}
                        </p>
                      </div>
                      <div>
                        <span className="text-gray-500">Staff Member:</span>
                        <p className="font-medium">{quote.staff_name || 'Unknown'}</p>
                        <p className="text-xs text-gray-400">{quote.company_name}</p>
                      </div>
                      <div>
                        <span className="text-gray-500">Service Location:</span>
                        <p className="font-medium">
                          {[
                            quote.farm_details?.address_number,
                            quote.farm_details?.address_road,
                            quote.farm_details?.address_town,
                            quote.farm_details?.address_region,
                            quote.farm_details?.address_postcode
                          ].filter(Boolean).join(', ') || quote.farm_details?.site_address || 'Not specified'}
                        </p>
                      </div>
                      <div>
                        <span className="text-gray-500">Total Value:</span>
                        <p className="font-bold text-[#1a4d2e]">
                          ${quote.total_cost_incl_gst?.toFixed(2) || '0.00'}
                        </p>
                      </div>
                    </div>
                    <div className="mt-3">
                      <span className="text-gray-500 text-sm">Services:</span>
                      <div className="flex flex-wrap gap-2 mt-1">
                        {quote.services?.map((svc, idx) => (
                          <span key={idx} className="bg-gray-100 text-gray-700 px-2 py-1 rounded text-xs">
                            {svc.service_name}: {svc.num_animals} animals
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                  <div className="flex lg:flex-col gap-2 lg:w-32">
                    <button
                      onClick={() => openCorporateQuoteWindow(quote)}
                      data-testid={`view-draft-${quote.id}`}
                      className="flex-1 lg:w-full px-3 py-2 bg-[#1a4d2e] text-white rounded-lg hover:bg-[#2d5a3d] transition-colors font-medium text-sm flex items-center justify-center gap-1"
                    >
                      <FileText className="h-4 w-4" />
                      View
                    </button>
                    <button
                      onClick={async () => {
                        if (window.confirm(`Delete this draft quote for "${quote.farm_details?.farm_name || 'Unnamed'}"?`)) {
                          try {
                            const response = await fetch(`${BACKEND_URL}/api/admin/unsubmitted-quotes/${quote.id}`, { method: 'DELETE' });
                            if (response.ok) {
                              fetchUnsubmittedQuotes();
                            } else {
                              alert('Error deleting quote');
                            }
                          } catch (err) {
                            alert('Error: ' + err.message);
                          }
                        }
                      }}
                      data-testid={`delete-draft-${quote.id}`}
                      className="flex-1 lg:w-full px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium text-sm flex items-center justify-center gap-1"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default DraftsTab;
